import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  RefreshCw as RefreshCwIcon,
  Sparkles as SparklesIcon,
} from 'lucide-react';
import AlphaIBoxOutlineIcon from 'mdi-react/AlphaIBoxOutlineIcon';
import MdiGroupIcon from 'mdi-react/GroupIcon';
import InfoIcon from 'mdi-react/InfoCircleOutlineIcon';
import { api } from 'services/api';
import { shouldTreatSuggestedAsCompositionOriginal } from 'shared/budget-component-type';
import { formatDate } from 'shared/date';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Box,
  SimpleGrid,
  Button,
  Icon,
  Checkbox,
  useMediaQuery,
} from '@chakra-ui/react';

import Loader from 'components/Loader';
import BudgetPlanningPreviewStateAlert from 'components/Modal/BudgetPlanningPreview/StateAlert';

import useAlert from 'hooks/useAlert';

import {
  BudgetComponent,
  FlattenedRow,
  ModalBudgetPlanningPreviewProps,
} from './types';
import { buildTreeFromMap, flattenTree, isStage } from './utils';

const currencyFormatter = Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const quantityFormatter = Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 4,
});

const percentFormatter = Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});

const modalMargin = 120;
const modalMobileFix = 100;

const MAX_SIENGE_STAGE_LEVEL = 4;
const MAX_CONCURRENT_TREE_REQUESTS = 6;
const MAX_COMPONENT_FETCH_RETRIES = 2;
const COMPONENT_FETCH_RETRY_DELAY_MS = 250;

const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const ModalBudgetPlanningPreview: React.FC<ModalBudgetPlanningPreviewProps> = ({
  data: {
    budget,
    selectable = false,
    selectionType = 'all_items',
    isTargetSiengeBudget = false,
    targetStageLevel = null,
    targetBudget,
    targetBudgetId,
    targetStageId,
  },
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const { openAlert } = useAlert();
  const [isMobile] = useMediaQuery('(max-width: 728px)');
  const [loading, setLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<FlattenedRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const budgetId = budget?.id;
  const isSiengeBudget = budget?.integrated_sienge === true;
  const isStagesOnlySelection = selectionType === 'stages_only';
  const resolvedTargetBudgetId = targetBudgetId || targetBudget?.id;
  const targetBudgetDescription =
    targetBudget?.identification?.description || 'de destino';

  const requestBudgetComponents = useCallback(
    async (
      params: Record<string, string | number>,
      retryAttempt = 0,
    ): Promise<BudgetComponent[]> => {
      try {
        const { data: response } = await api.get(
          `/budget/${budgetId}/component`,
          {
            params,
          },
        );

        return response.data || [];
      } catch (err) {
        if (retryAttempt >= MAX_COMPONENT_FETCH_RETRIES) {
          throw err;
        }

        await wait(COMPONENT_FETCH_RETRY_DELAY_MS * (retryAttempt + 1));
        return requestBudgetComponents(params, retryAttempt + 1);
      }
    },
    [budgetId],
  );

  const getChildrenByParent = useCallback(
    async (parentStageId: number): Promise<BudgetComponent[]> => {
      return requestBudgetComponents({
        'filter[parent_stage_id]': parentStageId,
        sort: 'position',
      });
    },
    [requestBudgetComponents],
  );

  const loadStageLevelWithQueue = useCallback(
    async (
      parentStageIds: number[],
      componentMap: Map<number, BudgetComponent[]>,
    ): Promise<number[]> => {
      if (parentStageIds.length === 0) {
        return [];
      }

      const responses: Array<{
        parentStageId: number;
        children: BudgetComponent[];
      }> = [];
      const workerCount = Math.min(
        MAX_CONCURRENT_TREE_REQUESTS,
        parentStageIds.length,
      );

      const runWorker = async (
        currentIndex: number,
        workerStride: number,
      ): Promise<void> => {
        if (currentIndex >= parentStageIds.length) {
          return;
        }

        const parentStageId = parentStageIds[currentIndex];
        const children = await getChildrenByParent(parentStageId);

        responses[currentIndex] = {
          parentStageId,
          children,
        };

        await runWorker(currentIndex + workerStride, workerStride);
      };

      await Promise.all(
        Array.from({ length: workerCount }, (_, workerIndex) =>
          runWorker(workerIndex, workerCount),
        ),
      );

      const nextLevelParentIds: number[] = [];

      responses.forEach(({ parentStageId, children }) => {
        componentMap.set(parentStageId, children);

        children.forEach((child) => {
          if (isStage(child)) {
            nextLevelParentIds.push(child.id);
          }
        });
      });

      return nextLevelParentIds;
    },
    [getChildrenByParent],
  );

  const loadDescendantLevels = useCallback(
    async (
      parentStageIds: number[],
      componentMap: Map<number, BudgetComponent[]>,
    ): Promise<void> => {
      const loadLevels = async (stageIdsByLevel: number[]): Promise<void> => {
        if (stageIdsByLevel.length === 0) {
          return;
        }

        const nextStageIds = await loadStageLevelWithQueue(
          stageIdsByLevel,
          componentMap,
        );

        await loadLevels(nextStageIds);
      };

      await loadLevels(parentStageIds);
    },
    [loadStageLevelWithQueue],
  );

  const loadTree = useCallback(async () => {
    if (!budgetId) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const rootStages = await requestBudgetComponents({
        'filter[no_parent]': '.',
        sort: 'created_at',
      });
      const componentMap = new Map<number, BudgetComponent[]>();
      componentMap.set(0, rootStages);

      const rootStageIds = rootStages
        .filter((component) => isStage(component))
        .map((component) => component.id);

      await loadDescendantLevels(rootStageIds, componentMap);

      const tree = buildTreeFromMap(componentMap, 0);
      setRows(flattenTree(tree));
    } catch (err) {
      setRows([]);
      setErrorMessage('Não foi possível carregar a EAP deste orçamento.');
      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar a EAP do orçamento.',
      });
    } finally {
      setLoading(false);
    }
  }, [budgetId, loadDescendantLevels, requestBudgetComponents]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [budgetId]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const selectedCount = selectedRows.size;

  const stageIds = useMemo(() => {
    return new Set(
      rows
        .filter(({ component }) => isStage(component))
        .map(({ component }) => component.id),
    );
  }, [rows]);

  const directChildrenByParentStageId = useMemo(() => {
    const map = new Map<number, number[]>();

    rows.forEach(({ component }) => {
      if (component.parent_stage_id === undefined) {
        return;
      }

      const currentChildren = map.get(component.parent_stage_id) || [];
      currentChildren.push(component.id);
      map.set(component.parent_stage_id, currentChildren);
    });

    return map;
  }, [rows]);

  const descendantIdsByStageId = useMemo(() => {
    const cache = new Map<number, number[]>();

    const collectDescendants = (stageId: number): number[] => {
      const cached = cache.get(stageId);
      if (cached) {
        return cached;
      }

      const directChildren = directChildrenByParentStageId.get(stageId) || [];
      const descendants = [...directChildren];

      directChildren.forEach((childId) => {
        if (stageIds.has(childId)) {
          descendants.push(...collectDescendants(childId));
        }
      });

      cache.set(stageId, descendants);
      return descendants;
    };

    stageIds.forEach((stageId) => {
      collectDescendants(stageId);
    });

    return cache;
  }, [directChildrenByParentStageId, stageIds]);

  const stageDepthByStageId = useMemo(() => {
    const cache = new Map<number, number>();

    const getStageDepth = (stageId: number): number => {
      const cachedDepth = cache.get(stageId);
      if (cachedDepth !== undefined) {
        return cachedDepth;
      }

      const directChildren = directChildrenByParentStageId.get(stageId) || [];
      const directStageChildren = directChildren.filter((childId) =>
        stageIds.has(childId),
      );

      if (directStageChildren.length === 0) {
        cache.set(stageId, 1);
        return 1;
      }

      const maxChildDepth = Math.max(
        ...directStageChildren.map((childId) => getStageDepth(childId)),
      );
      const depth = maxChildDepth + 1;

      cache.set(stageId, depth);
      return depth;
    };

    stageIds.forEach((stageId) => {
      getStageDepth(stageId);
    });

    return cache;
  }, [directChildrenByParentStageId, stageIds]);

  const canSelectStageForTargetLevel = useCallback(
    (componentId: number): boolean => {
      if (!isTargetSiengeBudget) {
        return true;
      }

      if (targetStageLevel === null || targetStageLevel === undefined) {
        return true;
      }

      const allowedStageDepth = MAX_SIENGE_STAGE_LEVEL - targetStageLevel;
      if (allowedStageDepth < 1) {
        return false;
      }

      return (stageDepthByStageId.get(componentId) || 1) <= allowedStageDepth;
    },
    [isTargetSiengeBudget, stageDepthByStageId, targetStageLevel],
  );

  const isImportableComponent = useCallback(
    (component: BudgetComponent): boolean => {
      if (!selectable) {
        return false;
      }

      if (component.type === 'unit_constructive') {
        return false;
      }

      if (
        isTargetSiengeBudget &&
        (component.type === 'input_original' ||
          component.type === 'composition_custom')
      ) {
        return false;
      }

      if (isStage(component) && !canSelectStageForTargetLevel(component.id)) {
        return false;
      }

      return true;
    },
    [canSelectStageForTargetLevel, isTargetSiengeBudget, selectable],
  );

  const isSelectableComponent = useCallback(
    (component: BudgetComponent): boolean => {
      if (!isImportableComponent(component)) {
        return false;
      }

      if (isStagesOnlySelection && !isStage(component)) {
        return false;
      }

      return true;
    },
    [isImportableComponent, isStagesOnlySelection],
  );

  const selectableRowIds = useMemo(() => {
    return rows
      .filter(({ component }) => isSelectableComponent(component))
      .map(({ component }) => component.id);
  }, [rows, isSelectableComponent]);

  const selectableRowIdSet = useMemo(() => {
    return new Set(selectableRowIds);
  }, [selectableRowIds]);

  const importableRowIds = useMemo(() => {
    return rows
      .filter(({ component }) => isImportableComponent(component))
      .map(({ component }) => component.id);
  }, [rows, isImportableComponent]);

  const importableRowIdSet = useMemo(() => {
    return new Set(importableRowIds);
  }, [importableRowIds]);

  const lockedDescendantIds = useMemo(() => {
    const ids = new Set<number>();

    selectedRows.forEach((selectedId) => {
      if (!stageIds.has(selectedId)) {
        return;
      }

      const descendants = descendantIdsByStageId.get(selectedId) || [];
      descendants.forEach((childId) => {
        ids.add(childId);
      });
    });

    return ids;
  }, [selectedRows, stageIds, descendantIdsByStageId]);

  const selectedComponentIds = useMemo(() => {
    return Array.from(selectedRows);
  }, [selectedRows]);

  const selectedComponents = useMemo(() => {
    return rows
      .filter((row) => selectedRows.has(row.component.id))
      .map((row) => row.component);
  }, [rows, selectedRows]);

  const toggleRowSelection = (componentId: number): void => {
    const isStageComponent = stageIds.has(componentId);

    setSelectedRows((previous) => {
      if (isImporting) {
        return previous;
      }

      if (!selectableRowIdSet.has(componentId)) {
        return previous;
      }

      if (lockedDescendantIds.has(componentId)) {
        return previous;
      }

      const next = new Set(previous);
      const descendants = descendantIdsByStageId.get(componentId) || [];

      if (next.has(componentId)) {
        next.delete(componentId);

        if (isStageComponent) {
          descendants.forEach((childId) => {
            if (importableRowIdSet.has(childId)) {
              next.delete(childId);
            }
          });
        }
      } else {
        next.add(componentId);

        if (isStageComponent) {
          descendants.forEach((childId) => {
            if (importableRowIdSet.has(childId)) {
              next.add(childId);
            }
          });
        }
      }

      return next;
    });
  };

  const toggleAllRowsSelection = (): void => {
    setSelectedRows((previous) => {
      if (isImporting) {
        return previous;
      }

      const allSelectableSelected = selectableRowIds.every((id) =>
        previous.has(id),
      );

      if (allSelectableSelected) {
        return new Set<number>();
      }

      const next = new Set<number>();

      selectableRowIds.forEach((componentId) => {
        next.add(componentId);

        if (stageIds.has(componentId)) {
          const descendants = descendantIdsByStageId.get(componentId) || [];
          descendants.forEach((childId) => {
            if (importableRowIdSet.has(childId)) {
              next.add(childId);
            }
          });
        }
      });

      return next;
    });
  };

  const getTypeIcon = (component: BudgetComponent): React.ReactNode => {
    if (component.type === 'unit_constructive') {
      return null;
    }

    if (component.type === 'input_original') {
      return <Icon w={5} h={5} as={InfoIcon} />;
    }

    if (
      shouldTreatSuggestedAsCompositionOriginal({
        type: component.type,
        total: component.total || 0,
      })
    ) {
      const compositionKind = component.composition_price?.composition?.kind;

      return compositionKind === 'informed' ? (
        <Icon w={5} h={5} as={AlphaIBoxOutlineIcon} />
      ) : (
        <Icon w={5} h={5} as={MdiGroupIcon} />
      );
    }

    if (component.type === 'suggested_composition') {
      return <Icon w={5} h={5} as={SparklesIcon} />;
    }

    if (component.type === 'composition_custom') {
      return <Icon w={5} h={5} as={AlphaIBoxOutlineIcon} />;
    }

    return null;
  };

  const getBaseAndReference = (component: BudgetComponent): string => {
    if (component.type === 'composition_custom') {
      return 'Preço Informado';
    }

    if (component.input_price) {
      return component.input_price.base?.description || 'Não definido';
    }

    if (component.composition_price) {
      return component.composition_price.base?.description || 'Não definido';
    }

    return '-';
  };

  const getCode = (component: BudgetComponent): string => {
    if (component.input_price) {
      return component.input_price.input?.code || 'Não definido';
    }

    if (component.composition_price) {
      return component.composition_price.composition?.code || 'Não definido';
    }

    if (component.budget_composition_price) {
      return component.budget_composition_price.code || 'Não definido';
    }

    return '-';
  };

  const getDescription = (component: BudgetComponent): string => {
    if (isStage(component)) {
      return component.title || 'Sem descrição';
    }

    if (component.input_price) {
      return (
        component.input_price.custom_description ||
        component.input_price.input?.description ||
        'Não definido'
      );
    }

    if (component.composition_price) {
      return (
        component.composition_price.custom_description ||
        component.composition_price.composition?.description ||
        component.description ||
        'Não definido'
      );
    }

    if (component.budget_composition_price) {
      return component.budget_composition_price.description || 'Não definido';
    }

    return component.description || 'Não definido';
  };

  const getUnitMeasure = (component: BudgetComponent): string => {
    if (component.input_price) {
      return component.input_price.input?.unit_measure?.description || '-';
    }

    if (component.composition_price) {
      return (
        component.composition_price.composition?.unit_measure?.description ||
        '-'
      );
    }

    if (component.budget_composition_price) {
      return (
        component.budget_composition_price.unit_measure_id?.description || '-'
      );
    }

    return '-';
  };

  const getUnitPrice = (component: BudgetComponent): number => {
    return component.social_law_price || 0;
  };

  const getPriceWithBDI = (component: BudgetComponent): number => {
    if (typeof component.price === 'number') {
      return component.price;
    }

    if (
      typeof component.total_bdi === 'number' &&
      typeof component.quantity === 'number' &&
      component.quantity > 0
    ) {
      return component.total_bdi / component.quantity;
    }

    return 0;
  };

  const getItemLocation = (component: BudgetComponent): string => {
    if (component.type === 'unit_constructive') {
      return '';
    }

    if (isSiengeBudget) {
      return component.sienge_location || '';
    }

    if (
      component.location !== undefined &&
      component.location !== null &&
      component.location !== 0
    ) {
      return String(component.location);
    }

    return '';
  };

  const validateSelection = useCallback((): boolean => {
    if (
      !resolvedTargetBudgetId ||
      targetStageId === undefined ||
      Number.isNaN(targetStageId)
    ) {
      toast({
        status: 'error',
        description:
          'Não foi possível identificar o orçamento ou etapa de destino para a importação.',
      });
      return false;
    }

    if (selectedComponentIds.length === 0) {
      toast({
        status: 'error',
        description: 'Selecione ao menos um item para importar.',
      });
      return false;
    }

    const hasInvalidSelection = selectedComponents.some(
      (component) => !isImportableComponent(component),
    );

    if (hasInvalidSelection) {
      toast({
        status: 'error',
        description:
          'A seleção possui componentes inválidos para o orçamento de destino. Revise os itens selecionados.',
      });
      return false;
    }
    return true;
  }, [
    isImportableComponent,
    resolvedTargetBudgetId,
    selectedComponentIds.length,
    selectedComponents,
    targetStageId,
  ]);

  const importSelectedComponents = useCallback(async (): Promise<void> => {
    await api.post(`/budget/${resolvedTargetBudgetId}/component/import`, {
      source_budget_id: budgetId,
      parent_stage_id: targetStageId ?? null,
      budget_component_ids: selectedComponentIds,
    });
  }, [budgetId, resolvedTargetBudgetId, selectedComponentIds, targetStageId]);

  const handleImportSelection = useCallback(async (): Promise<void> => {
    if (isImporting) {
      return;
    }

    if (!validateSelection()) {
      return;
    }

    setIsImporting(true);

    try {
      await importSelectedComponents();

      toast({
        status: 'success',
        description: 'Itens importados com sucesso.',
      });

      if (onConfirm) {
        onConfirm({
          selectedComponentIds,
          selectedComponents,
        });
      }

      handleClose();
    } catch (err) {
      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          'Houve um erro ao importar os itens para o orçamento de destino.',
      });
    } finally {
      setIsImporting(false);
    }
  }, [
    handleClose,
    importSelectedComponents,
    isImporting,
    onConfirm,
    selectedComponentIds,
    selectedComponents,
    validateSelection,
  ]);

  const handleConfirmSelection = useCallback((): void => {
    if (isImporting) {
      return;
    }

    openAlert({
      size: '2xl',
      type: 'info',
      title: 'Confirmar importação',
      description: () => (
        <Text>
          Tem certeza que deseja importar esses {selectedCount} itens para o
          orçamento {targetBudgetDescription}?
        </Text>
      ),
      buttons: ['Cancelar', 'Confirmar'],
      onConfirm: handleImportSelection,
    });
  }, [
    handleImportSelection,
    isImporting,
    openAlert,
    selectedCount,
    targetBudgetDescription,
  ]);

  const allSelectableSelected =
    selectableRowIds.length > 0 &&
    selectableRowIds.every((id) => selectedRows.has(id));

  return (
    <Modal
      {...restProps}
      scrollBehavior="inside"
      closeOnEsc={!isImporting}
      closeOnOverlayClick={!isImporting}
    >
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>EAP do Orçamento</ModalHeader>

        <ModalCloseButton isDisabled={isImporting} />

        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={4}>
            <Box>
              <Text fontSize="sm" color="gray.600">
                Descrição
              </Text>
              <Text fontWeight="bold">
                {budget.identification?.description || '-'}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600">
                Cliente / Obra
              </Text>
              <Text fontWeight="bold">
                {budget.identification?.client?.name ||
                  budget.identification?.client?.fantasy_name ||
                  budget.identification?.enterprise?.name ||
                  '-'}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600">
                Criação
              </Text>
              <Text>{formatDate(budget.created_at)}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600">
                Total
              </Text>
              <Text>{currencyFormatter.format(budget.total || 0)}</Text>
            </Box>
          </SimpleGrid>

          {selectable && (
            <Box
              w="100%"
              border="1px solid"
              borderColor="gray.100"
              borderLeftWidth="3px"
              borderLeftColor="gray.300"
              borderRadius="8px"
              background="gray.50"
              p={3}
              mb={loading || !!errorMessage ? 2 : 4}
            >
              <Text fontSize="md" fontWeight="semibold" color="gray.700" mb={2}>
                Selecionar itens
              </Text>

              <Box as="ul" pl={5} color="gray.700" fontSize="md">
                <Text as="li" whiteSpace="pre-wrap">
                  Selecione os itens deste orçamento que deseja importar para o
                  orçamento{' '}
                  <Text as="span" fontWeight="bold">
                    {targetBudgetDescription}
                  </Text>
                  .
                </Text>

                <Text as="li" mt={1} whiteSpace="pre-wrap">
                  Ao selecionar uma etapa, todos os itens vinculados a ela
                  também serão importados, incluindo subetapas e seus conteúdos.
                </Text>

                <Text as="li" mt={1} whiteSpace="pre-wrap">
                  Ao selecionar composições, apenas a estrutura será importada.
                  Os custos serão recalculados com base nos valores do orçamento
                  de destino
                </Text>

                {isStagesOnlySelection && (
                  <Text as="li" mt={1} whiteSpace="pre-wrap">
                    Neste fluxo, apenas etapas podem ser selecionadas
                    manualmente. Os itens válidos dentro delas serão
                    selecionados automaticamente para importação.
                  </Text>
                )}

                {isTargetSiengeBudget && (
                  <>
                    <Text as="li" mt={1} whiteSpace="pre-wrap">
                      Não é possível importar insumos diretamente e serviços com
                      preço informado de um orçamento comum para um orçamento
                      Sienge.
                    </Text>

                    <Text as="li" mt={1} whiteSpace="pre-wrap">
                      Não é possível importar etapas que, após a importação,
                      violem as regras estruturais do orçamento Sienge
                      (resultando em mais de 4 níveis de profundidade).
                      <Text
                        as="span"
                        display="block"
                        fontSize="xs"
                        color="gray.600"
                        mt={1}
                      >
                        Você ainda poderá importar os itens internos dessas
                        etapas, desde que respeitem as regras do orçamento.
                      </Text>
                    </Text>
                  </>
                )}
              </Box>
            </Box>
          )}

          {loading && (
            <BudgetPlanningPreviewStateAlert
              variant="info"
              title="Carregando orçamento"
              description="Carregando a EAP do orçamento. Esse processo pode demorar alguns instantes."
            />
          )}

          {!loading && errorMessage && (
            <BudgetPlanningPreviewStateAlert
              variant="error"
              title="Falha ao carregar a EAP"
              description={errorMessage}
              actionLabel="Tentar novamente"
              onAction={loadTree}
            />
          )}

          <TableContainer border="1px solid" borderColor="gray.100">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  {selectable && (
                    <Th width="45px" textAlign="center">
                      <Checkbox
                        title="Selecionar todos"
                        isChecked={allSelectableSelected}
                        onChange={toggleAllRowsSelection}
                        isDisabled={isImporting}
                      />
                    </Th>
                  )}
                  <Th width="65px" textAlign="center">
                    Item
                  </Th>
                  <Th width="140px" textAlign="center">
                    Base
                  </Th>
                  <Th width="100px">Código</Th>
                  <Th>Descrição</Th>
                  <Th width="80px">Un</Th>
                  <Th width="90px" isNumeric>
                    Qtde
                  </Th>
                  <Th width="110px" isNumeric>
                    Unit.
                  </Th>
                  <Th width="85px" isNumeric>
                    BDI
                  </Th>
                  <Th width="110px" isNumeric>
                    Com BDI
                  </Th>
                  <Th width="120px" isNumeric>
                    Total
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {loading && (
                  <Tr>
                    <Td
                      colSpan={selectable ? 11 : 10}
                      textAlign="center"
                      py={6}
                    >
                      <Loader />
                    </Td>
                  </Tr>
                )}

                {!loading && !errorMessage && rows.length === 0 && (
                  <Tr>
                    <Td colSpan={selectable ? 11 : 10} py={6}>
                      Nenhum componente encontrado para este orçamento.
                    </Td>
                  </Tr>
                )}

                {!loading &&
                  !errorMessage &&
                  rows.map(({ component, depth }) => {
                    const isStageRow = isStage(component);

                    return (
                      <Tr
                        key={component.id}
                        className={
                          selectedRows.has(component.id)
                            ? 'selected-row'
                            : undefined
                        }
                      >
                        {selectable && (
                          <Td textAlign="center">
                            <Checkbox
                              isChecked={selectedRows.has(component.id)}
                              onChange={() => toggleRowSelection(component.id)}
                              title="Selecionar componente"
                              isDisabled={
                                isImporting ||
                                !isSelectableComponent(component) ||
                                (selectedRows.has(component.id) &&
                                  lockedDescendantIds.has(component.id))
                              }
                            />
                          </Td>
                        )}

                        <Td textAlign="center">
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            fontWeight="bold"
                          >
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="center"
                              gap={1}
                            >
                              {isStageRow && <Icon as={ChevronDownIcon} />}
                              <Text as="span">
                                {getItemLocation(component)}
                              </Text>
                            </Box>

                            {!isStageRow && getTypeIcon(component)}
                          </Box>
                        </Td>

                        {isStageRow ? (
                          <Td colSpan={3}>
                            <Box pl={depth * 5}>
                              <Text fontWeight="bold" whiteSpace="pre-wrap">
                                {getDescription(component)}
                              </Text>
                            </Box>
                          </Td>
                        ) : (
                          <>
                            <Td textAlign="center">
                              {getBaseAndReference(component)}
                            </Td>

                            <Td>{getCode(component)}</Td>

                            <Td>
                              <Box pl={depth * 5}>
                                <Text whiteSpace="pre-wrap">
                                  {getDescription(component)}
                                </Text>
                              </Box>
                            </Td>
                          </>
                        )}

                        <Td>{!isStageRow && getUnitMeasure(component)}</Td>

                        <Td isNumeric>
                          {!isStageRow &&
                            quantityFormatter.format(
                              component.recursive_quantity ||
                                component.quantity ||
                                0,
                            )}
                        </Td>

                        <Td isNumeric>
                          {!isStageRow &&
                            currencyFormatter.format(getUnitPrice(component))}
                        </Td>

                        <Td isNumeric>
                          {!isStageRow &&
                          component.bdi_percent !== null &&
                          component.bdi_percent !== undefined
                            ? percentFormatter.format(component.bdi_percent)
                            : null}
                        </Td>

                        <Td isNumeric>
                          {!isStageRow &&
                            currencyFormatter.format(
                              getPriceWithBDI(component),
                            )}
                        </Td>

                        <Td isNumeric>
                          {currencyFormatter.format(component.total || 0)}
                        </Td>
                      </Tr>
                    );
                  })}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isImporting}
          >
            Fechar
          </Button>

          <Button
            variant="outline"
            leftIcon={<Icon as={RefreshCwIcon} />}
            onClick={loadTree}
            isLoading={loading}
            isDisabled={isImporting}
            loadingText="Recarregando"
            mr={selectable ? 3 : 0}
          >
            Recarregar EAP
          </Button>

          {selectable && (
            <Button
              colorScheme="green"
              leftIcon={<Icon as={CheckIcon} />}
              onClick={handleConfirmSelection}
              isDisabled={selectedCount === 0 || isImporting}
              isLoading={isImporting}
              loadingText="Importando"
            >
              Selecionar ({selectedCount})
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetPlanningPreview;
